'use client';

import { Button } from '@/components/ui/button';
import { useGetMetaIntegration } from '@/features/integrations/api';
import { MetaConnectSelectionDialog } from '@/features/integrations/components';
import { useMetaLoginForBusiness } from '@/features/integrations/hooks/use-meta-login-for-business';
import {
  useCreateAccountLink,
  useGetAccountStatus,
  useRefreshAccountStatus,
} from '@/features/stripe-connect/api';
import { cn } from '@/lib/utils';
import { webAppUrl } from '@/lib/web-app-origin';
import { CheckCircle2Icon, CreditCardIcon, MegaphoneIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

type SlideKey = 'meta' | 'stripe' | 'intro' | 'onboarding' | 'done';
const SLIDES: SlideKey[] = ['meta', 'stripe', 'intro', 'onboarding', 'done'];

/** Booked calls and skipped slides, remembered in this browser only. */
const storeKey = (token: string) => `borrah-setup:${token}`;
const readStore = (token: string): Partial<Record<SlideKey, true>> => {
  try {
    return JSON.parse(localStorage.getItem(storeKey(token)) ?? '{}');
  } catch {
    return {};
  }
};
const writeStore = (token: string, value: Partial<Record<SlideKey, true>>) => {
  try {
    localStorage.setItem(storeKey(token), JSON.stringify(value));
  } catch {
    // private window: the slides still work, they just aren't remembered
  }
};

/** A full-page slide: one job, one primary action, and a way to skip it. */
function Slide({
  icon,
  title,
  children,
  action,
  onSkip,
  done,
}: {
  icon?: React.ReactNode;
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  onSkip?: () => void;
  done?: boolean;
}) {
  return (
    <section className="flex min-h-[60dvh] flex-col items-center justify-center gap-6 rounded-xl border bg-card px-6 py-12 text-center">
      {icon && (
        <div className="flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          {done ? <CheckCircle2Icon className="size-7" /> : icon}
        </div>
      )}
      <div className="max-w-md space-y-2">
        <h2 className="text-2xl font-semibold tracking-tight text-balance">
          {title}
        </h2>
        <div className="text-muted-foreground">{children}</div>
      </div>
      {action}
      {onSkip && (
        <button
          type="button"
          onClick={onSkip}
          className="text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          {done ? 'Continue' : 'Skip for now'}
        </button>
      )}
    </section>
  );
}

function MetaSlide({ onNext }: { onNext: () => void }) {
  const { isConnected, isPendingConfiguration, isLoading } =
    useGetMetaIntegration();
  const [picking, setPicking] = useState(false);
  const { launch, isReady, isBusy } = useMetaLoginForBusiness({
    // Connecting stores what the login can reach; they pick the Page and ad account next.
    onSuccess: () => setPicking(true),
  });
  // Back from a redirect with the choice still to make: ask once.
  const asked = useRef(false);
  useEffect(() => {
    if (!isLoading && isPendingConfiguration && !asked.current) {
      asked.current = true;
      setPicking(true);
    }
  }, [isLoading, isPendingConfiguration]);
  return (
    <>
      <Slide
        icon={<MegaphoneIcon className="size-7" />}
        title={
          isConnected
            ? 'Facebook and Instagram are connected'
            : 'Connect Facebook and Instagram'
        }
        done={isConnected}
        onSkip={onNext}
        action={
          isConnected ? (
            <Button size="lg" onClick={onNext}>
              Next
            </Button>
          ) : (
            <Button
              size="lg"
              disabled={!isReady || isBusy}
              onClick={() => launch()}
            >
              {isBusy ? 'Connecting…' : 'Connect with Facebook'}
            </Button>
          )
        }
      >
        {isConnected
          ? 'We can run your ads and reply to your leads from here.'
          : 'This is how we run your ads and reply to the people who message your clinic. You’ll choose your Facebook Page and ad account next.'}
      </Slide>
      <MetaConnectSelectionDialog
        open={picking}
        onOpenChange={setPicking}
        onSuccess={() => setPicking(false)}
      />
    </>
  );
}

function StripeSlide({ token, onNext }: { token: string; onNext: () => void }) {
  const { status } = useGetAccountStatus();
  const { createAccountLinkAsync, isCreating } = useCreateAccountLink();
  const { refreshAccountStatus } = useRefreshAccountStatus();
  // Back from Stripe's pages: read the account live (its webhook may be late).
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('stripe') === 'return')
      refreshAccountStatus();
  }, [refreshAccountStatus]);
  const ready = !!status?.chargesEnabled;
  const start = async () => {
    try {
      const { url } = await createAccountLinkAsync({
        baseUrl: webAppUrl(`/start/${encodeURIComponent(token)}`),
      });
      window.location.href = url;
    } catch {
      toast.error('Couldn’t open Stripe. Please try again.');
    }
  };
  return (
    <Slide
      icon={<CreditCardIcon className="size-7" />}
      title={ready ? 'Stripe is set up' : 'Set up payments with Stripe'}
      done={ready}
      onSkip={onNext}
      action={
        ready ? (
          <Button size="lg" onClick={onNext}>
            Next
          </Button>
        ) : (
          <Button size="lg" disabled={isCreating} onClick={() => void start()}>
            {isCreating
              ? 'Opening Stripe…'
              : status?.connected
                ? 'Finish setting up Stripe'
                : 'Set up Stripe'}
          </Button>
        )
      }
    >
      {ready
        ? 'Clients can pay deposits and bookings online, straight to your account.'
        : 'So clients can pay deposits and bookings online. Stripe asks for your business and bank details, then brings you back here.'}
    </Slide>
  );
}

type CalWindow = Window & {
  Cal?: ((...args: unknown[]) => void) & {
    loaded?: boolean;
    ns: Record<string, (...args: unknown[]) => void>;
    q?: unknown[];
  };
};

/** Cal.com's own loader (their embed snippet), so `Cal(...)` works before the script lands. */
const loadCal = () => {
  const w = window as CalWindow;
  if (w.Cal) return w.Cal;
  const push = (api: { q: unknown[] }, args: unknown) => api.q.push(args);
  const cal = function (this: unknown, ...args: unknown[]) {
    const c = w.Cal as NonNullable<CalWindow['Cal']> & { q: unknown[] };
    if (!c.loaded) {
      c.ns = {};
      c.q = c.q || [];
      const s = document.createElement('script');
      s.src = 'https://app.cal.com/embed/embed.js';
      document.head.appendChild(s);
      c.loaded = true;
    }
    if (args[0] === 'init') {
      const api = Object.assign((...a: unknown[]) => push(api, a), {
        q: [] as unknown[],
      });
      const namespace = args[1];
      if (typeof namespace === 'string') {
        c.ns[namespace] = c.ns[namespace] || api;
        push(c.ns[namespace] as never, args);
        push(c, ['initNamespace', namespace]);
      } else push(c, args);
      return;
    }
    push(c, args);
  } as NonNullable<CalWindow['Cal']>;
  w.Cal = cal;
  return cal;
};

/** A Cal.com booking page, inline; `onBooked` fires when they book. */
function CalEmbed({
  link,
  name,
  email,
  onBooked,
}: {
  link: string;
  name: string | null;
  email: string | null;
  onBooked: () => void;
}) {
  const id = useMemo(() => `cal-${Math.random().toString(36).slice(2)}`, []);
  const calLink = useMemo(
    () => new URL(link).pathname.replace(/^\//, ''),
    [link]
  );
  const booked = useRef(onBooked);
  booked.current = onBooked;
  useEffect(() => {
    const Cal = loadCal();
    Cal('init', id, { origin: 'https://cal.com' });
    const ns = () => (window as CalWindow).Cal?.ns[id];
    ns()?.('inline', {
      elementOrSelector: `#${id}`,
      calLink,
      config: {
        layout: 'month_view',
        ...(name ? { name } : {}),
        ...(email ? { email } : {}),
      },
    });
    ns()?.('on', {
      action: 'bookingSuccessful',
      callback: () => booked.current(),
    });
  }, [id, calLink, name, email]);
  return <div id={id} className="min-h-[640px] w-full overflow-auto" />;
}

function CallSlide({
  title,
  intro,
  link,
  name,
  email,
  booked,
  onBooked,
  onNext,
}: {
  title: string;
  intro: string;
  link: string;
  name: string | null;
  email: string | null;
  booked: boolean;
  onBooked: () => void;
  onNext: () => void;
}) {
  if (booked)
    return (
      <Slide
        icon={<CheckCircle2Icon className="size-7" />}
        title="Booked"
        done
        action={
          <Button size="lg" onClick={onNext}>
            Next
          </Button>
        }
      >
        You’ll get a calendar invite by email.
      </Slide>
    );
  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
        <p className="text-muted-foreground">{intro}</p>
      </div>
      <CalEmbed link={link} name={name} email={email} onBooked={onBooked} />
      <div className="text-center">
        <button
          type="button"
          onClick={onNext}
          className="text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          Skip for now
        </button>
      </div>
    </section>
  );
}

/**
 * After paying: connect Meta, set up Stripe, then book the intro call with
 * their CSM and the onboarding call with Eoin. Every slide can be skipped,
 * and the page reopens on the first one not yet done.
 */
export function SetupSlides({
  token,
  calls,
  name,
  email,
}: {
  token: string;
  calls: { intro: string; onboarding: string };
  name: string | null;
  email: string | null;
}) {
  const [seen, setSeen] = useState(() => readStore(token));
  const mark = (key: SlideKey) => {
    const next = { ...seen, [key]: true as const };
    setSeen(next);
    writeStore(token, next);
  };
  const returningFromStripe =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('stripe') === 'return';
  const [index, setIndex] = useState(() => {
    if (returningFromStripe) return SLIDES.indexOf('stripe');
    const first = SLIDES.findIndex((s) => !seen[s]);
    return first === -1 ? SLIDES.length - 1 : first;
  });
  const next = () => {
    mark(SLIDES[index]);
    setIndex((i) => Math.min(i + 1, SLIDES.length - 1));
  };
  const slide = SLIDES[index];

  return (
    <div className="space-y-6">
      <ol className="flex justify-center gap-2" aria-label="Setting up">
        {SLIDES.slice(0, -1).map((s, i) => (
          <li key={s}>
            <button
              type="button"
              aria-label={`Go to step ${i + 1}`}
              aria-current={i === index ? 'step' : undefined}
              onClick={() => setIndex(i)}
              className={cn(
                'h-1.5 w-8 rounded-full bg-muted transition-colors',
                (i < index || seen[s]) && 'bg-primary/40',
                i === index && 'bg-primary'
              )}
            />
          </li>
        ))}
      </ol>
      {slide === 'meta' && <MetaSlide onNext={next} />}
      {slide === 'stripe' && <StripeSlide token={token} onNext={next} />}
      {slide === 'intro' && (
        <CallSlide
          title="Book your welcome call"
          intro="A short call with your account manager, who looks after you from here on."
          link={calls.intro}
          name={name}
          email={email}
          booked={!!seen.intro}
          onBooked={() => mark('intro')}
          onNext={next}
        />
      )}
      {slide === 'onboarding' && (
        <CallSlide
          title="Book your onboarding call"
          intro="Eoin sets everything up with you: your services, your booking system and your first campaign."
          link={calls.onboarding}
          name={name}
          email={email}
          booked={!!seen.onboarding}
          onBooked={() => mark('onboarding')}
          onNext={next}
        />
      )}
      {slide === 'done' && (
        <Slide
          icon={<CheckCircle2Icon className="size-7" />}
          title="You’re all set"
          done
          action={
            <Button size="lg" asChild>
              <a href="/dashboard">Go to Borrah</a>
            </Button>
          }
        >
          Our team will be in touch before your first call. You can come back to
          this page any time to finish a step you skipped.
        </Slide>
      )}
    </div>
  );
}
